import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Error boundary primitive. Catches render-phase errors in its subtree and
 * renders a fallback. The fallback receives `error` and a `reset` function
 * the user can call to retry mounting children.
 *
 * Reset is also automatic when `resetKey` changes — useful for route-level
 * boundaries that should clear the error when the user navigates away:
 *
 *   <ErrorBoundary resetKey={location.pathname}>
 *     <Outlet />
 *   </ErrorBoundary>
 *
 * See frontend/FRONTEND_AGENT_RULES.md §6 — every major layout gets its own
 * boundary; one root-level boundary is not enough.
 */

type FallbackRender = (props: { error: Error; reset: () => void }) => ReactNode;

interface Props {
  children: ReactNode;
  fallback?: ReactNode | FallbackRender;
  resetKey?: unknown;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

function DefaultFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="flex flex-col h-full w-full items-center justify-center p-6 gap-4 text-center">
      <h2 className="text-2xl font-bold text-red-500">Something went wrong</h2>
      <p className="text-sm text-muted-foreground max-w-md">{error.message}</p>
      <button
        onClick={reset}
        className="px-4 py-2 border rounded hover:bg-muted"
        type="button"
      >
        Try again
      </button>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
    // Always log — losing the stack to a silent boundary is worse than noisy logs.
    console.error("[ErrorBoundary]", error, info);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error !== null && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { fallback } = this.props;
    if (typeof fallback === "function") {
      return fallback({ error, reset: this.reset });
    }
    if (fallback !== undefined) {
      return fallback;
    }
    return <DefaultFallback error={error} reset={this.reset} />;
  }
}
